import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, from } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { 
  MFAMethod, 
  MFAChallenge, 
  MFAResponse, 
  MFASetupResponse,
  UserMFASettings,
  MFAVerificationRequest,
  ResendCodeRequest
} from '../models/mfa.model';
import { environment } from '../../../../environments/environments';
import { API_ENDPOINTS } from '../../config/api-endpoints';
import { TOTP, generateSecret, verifySync } from 'otplib';
import * as QRCodeGenerator from 'qrcode-generator';

@Injectable({ providedIn: 'root' })
export class MFAService {
  
  // Signals for MFA state
  private _mfaRequired = signal(false);
  readonly mfaRequired = this._mfaRequired.asReadonly();

  private _activeMFAMethod = signal<MFAMethod | null>(null);
  readonly activeMFAMethod = this._activeMFAMethod.asReadonly();

  private _userMFASettings = signal<UserMFASettings | null>(null);
  readonly userMFASettings = this._userMFASettings.asReadonly();

  private _totpSecret = signal<string | null>(null);
  readonly totpSecret = this._totpSecret.asReadonly();

  // Track MFA session
  private _mfaSession = signal<string | null>(null);
  readonly mfaSession = this._mfaSession.asReadonly();

  private _mfaChallenge = signal<MFAChallenge | null>(null);
  readonly mfaChallenge = this._mfaChallenge.asReadonly();

  // Observable for MFA state changes
  private _mfaStateChange$ = new BehaviorSubject<{ stage: 'required' | 'verified' | 'setup' | 'none' }>({ stage: 'none' });
  public mfaStateChange$ = this._mfaStateChange$.asObservable();

  private readonly API_URL = `${environment.apiBaseUrl}/api/v1`;
  private readonly loginMfaSessionKey = 'bluedop_mfa_login_session';
  private readonly loginAccessTokenKey = 'bluedop_mfa_login_access_token';
  private readonly loginMfaUsernameKey = 'bluedop_mfa_login_username';
  private readonly loginMfaChallengeNameKey = 'bluedop_mfa_login_challenge_name';

  constructor(private http: HttpClient) {
    this.restoreMFAState();
  }

  /** Restore MFA state from localStorage */
  private restoreMFAState(): void {
    const savedSettings = localStorage.getItem('bluedop_mfa_settings');
    if (savedSettings) {
      try {
        this._userMFASettings.set(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Failed to restore MFA settings:', e);
      }
    }
  }

  /** ═════════════════════════════════════════════════════════
   * TOTP (Authenticator App) Management
   * ═════════════════════════════════════════════════════════ */

  /**
   * Generate TOTP secret and QR code for authenticator app setup
   * @param userId User identifier
   * @param email User email for authenticator label
   */
  generateTOTPSecret(userId: string, email: string): Observable<MFASetupResponse> {
    try {
      // Generate secret using otplib
      const secret = generateSecret();
      const totp = new TOTP();
      const appUri = `otpauth://totp/BlueDop%20EHR:${email}?secret=${secret}&issuer=BlueDop%20EHR`;

      // Generate QR code using qrcode-generator
      const qr = QRCodeGenerator.default(0, 'M');
      qr.addData(appUri);
      qr.make();
      const qrCode = qr.createDataURL(4, 0);

      this._totpSecret.set(secret);

      return this.http.post<MFASetupResponse>(
        `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_TOTP_SETUP}`,
        { userId, secret, qrCode }
      ).pipe(
        catchError(err => this.handleError(err))
      );
    } catch (error) {
      return throwError(() => new Error('Failed to generate TOTP secret'));
    }
  }

  setupTOTP(): Observable<MFASetupResponse> {
    const accessToken = this.getSetupAccessToken();
    const options = accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined;

    return this.http.post<any>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_TOTP_SETUP}`,
      {},
      options
    ).pipe(
      switchMap(response => {
        const data = response?.data ?? {};
        const otpauthUri = response?.otpauth_uri ?? response?.otpauthUri ?? data?.otpauth_uri ?? data?.otpauthUri;
        const sharedSecret = response?.sharedSecret ?? response?.shared_secret ?? response?.secret ?? data?.shared_secret ?? data?.secret;
        const sessionToken = response?.session_token ?? response?.sessionToken ?? data?.session_token ?? data?.sessionToken;

        if (!otpauthUri) {
          return throwError(() => new Error('Authenticator setup link was not returned by the server'));
        }

        if (sessionToken) {
          this.setLoginMFASession(sessionToken, MFAMethod.TOTP);
        }

        const qr = QRCodeGenerator.default(0, 'M');
        qr.addData(otpauthUri);
        qr.make();

        return [{
          ...response,
          otpauth_uri: otpauthUri,
          sharedSecret,
          session_token: sessionToken,
          qrCode: qr.createDataURL(4, 0),
        } as MFASetupResponse];
      }),
      catchError(err => this.handleError(err))
    );
  }

  verifyTOTPSetup(code: string, deviceName: string, sessionToken?: string): Observable<MFAResponse> {
    const accessToken = this.getSetupAccessToken();
    const options = accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined;
    const activeSessionToken = sessionToken || this.getLoginMFASession();

    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_TOTP_VERIFY}`,
      { code, device_name: deviceName, session_token: activeSessionToken },
      options
    ).pipe(
      catchError(err => this.handleError(err))
    );
  }

  setupWhatsApp(): Observable<MFASetupResponse> {
    return this.setupDeliveryMFA(MFAMethod.WHATSAPP);
  }

  setupSMS(): Observable<MFASetupResponse> {
    return this.setupDeliveryMFA(MFAMethod.SMS);
  }

  setupGmail(): Observable<MFASetupResponse> {
    return this.setupDeliveryMFA(MFAMethod.EMAIL);
  }

  private setupDeliveryMFA(method: MFAMethod): Observable<MFASetupResponse> {
    const accessToken = this.getSetupAccessToken();
    const options = accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined;

    return this.http.post<any>(
      `${this.API_URL}${this.getDeliverySetupEndpoint(method)}`,
      {},
      options
    ).pipe(
      tap(response => {
        const data = response?.data ?? {};
        const sessionToken = response?.session_token ?? response?.sessionToken ?? data?.session_token ?? data?.sessionToken;
        if (sessionToken) {
          this.setLoginMFASession(sessionToken, method);
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  verifyLoginTOTP(code: string, sessionToken?: string): Observable<any> {
    const accessToken = this.getSetupAccessToken();
    const options = accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined;
    const activeSessionToken = sessionToken || this.getLoginMFASession();

    return this.http.post(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_TOTP_VERIFY}`,
      {
        code,
        device_name: 'web',
        session_token: activeSessionToken,
      },
      options
    ).pipe(
      tap(() => this.clearMFAState()),
      catchError(err => this.handleError(err))
    );
  }

  verifyWhatsApp(code: string, sessionToken?: string): Observable<any> {
    return this.verifyDeliveryMFA(MFAMethod.WHATSAPP, code, sessionToken);
  }

  verifySMS(code: string, sessionToken?: string): Observable<any> {
    return this.verifyDeliveryMFA(MFAMethod.SMS, code, sessionToken);
  }

  verifyGmail(code: string, sessionToken?: string): Observable<any> {
    return this.verifyDeliveryMFA(MFAMethod.EMAIL, code, sessionToken);
  }

  private verifyDeliveryMFA(method: MFAMethod, code: string, sessionToken?: string): Observable<any> {
    const activeSessionToken = sessionToken || this.getLoginMFASession();
    const accessToken = this.getSetupAccessToken();
    const options = accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined;

    if (!activeSessionToken) {
      return throwError(() => new Error(`No active ${this.getDeliveryMethodLabel(method)} verification session`));
    }

    return this.http.post(
      `${this.API_URL}${this.getDeliveryVerifyEndpoint(method)}`,
      {
        code,
        session_token: activeSessionToken,
      },
      options
    ).pipe(
      tap(() => this.clearMFAState()),
      catchError(err => this.handleError(err))
    );
  }

  private getDeliverySetupEndpoint(method: MFAMethod): string {
    if (method === MFAMethod.SMS) return API_ENDPOINTS.AUTH.MFA_SMS_SETUP;
    if (method === MFAMethod.EMAIL) return API_ENDPOINTS.AUTH.MFA_GMAIL_SETUP;
    return API_ENDPOINTS.AUTH.MFA_WHATSAPP_SETUP;
  }

  private getDeliveryVerifyEndpoint(method: MFAMethod): string {
    if (method === MFAMethod.SMS) return API_ENDPOINTS.AUTH.MFA_SMS_VERIFY;
    if (method === MFAMethod.EMAIL) return API_ENDPOINTS.AUTH.MFA_GMAIL_VERIFY;
    return API_ENDPOINTS.AUTH.MFA_WHATSAPP_VERIFY;
  }

  private getDeliveryMethodLabel(method: MFAMethod): string {
    if (method === MFAMethod.SMS) return 'SMS';
    if (method === MFAMethod.EMAIL) return 'email';
    return 'WhatsApp';
  }

  setSetupAccessToken(accessToken: string): void {
    localStorage.setItem(this.loginAccessTokenKey, accessToken);
  }

  getSetupAccessToken(): string | null {
    return localStorage.getItem(this.loginAccessTokenKey)
      || localStorage.getItem('bluedop_token');
  }

  private getMFAActionToken(): string | null {
    return this.getSetupAccessToken() || this.getLoginMFASession();
  }

  setLoginMFASession(sessionToken: string, method: MFAMethod = MFAMethod.TOTP): void {
    this._mfaSession.set(sessionToken);
    this._mfaRequired.set(true);
    this._activeMFAMethod.set(method);
    localStorage.setItem(this.loginMfaSessionKey, sessionToken);
  }

  getLoginMFASession(): string | null {
    return this._mfaSession() || localStorage.getItem(this.loginMfaSessionKey);
  }

  getLoginMFAUsername(): string | null {
    return localStorage.getItem(this.loginMfaUsernameKey);
  }

  getLoginMFAChallengeName(): string | null {
    return localStorage.getItem(this.loginMfaChallengeNameKey);
  }

  tryAnotherWay(): Observable<MFAMethod[]> {
    const username = this.getLoginMFAUsername();
    const sessionToken = this.getLoginMFASession();

    if (!username || !sessionToken) {
      return throwError(() => new Error('No active MFA login session'));
    }

    return this.http.post<any>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.LOGIN_TRY_ANOTHER_WAY}`,
      {
        username,
        session_token: sessionToken,
      }
    ).pipe(
      switchMap(response => [this.normalizeAvailableMethods(response)]),
      catchError(err => this.handleError(err))
    );
  }

  initiateLoginRecovery(method: MFAMethod): Observable<any> {
    const username = this.getLoginMFAUsername();
    const sessionToken = this.getLoginMFASession();

    if (!username || !sessionToken) {
      return throwError(() => new Error('No active MFA login session'));
    }

    return this.http.post<any>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.LOGIN_RECOVERY}`,
      {
        username,
        channel: this.getRecoveryChallenge(method),
        session_token: sessionToken,
      }
    ).pipe(
      tap(response => this.updateLoginMFAContext(response, method)),
      catchError(err => this.handleError(err))
    );
  }

  verifyLoginMFA(code: string): Observable<any> {
    const sessionToken = this.getLoginMFASession();

    if (!sessionToken) {
      return throwError(() => new Error('No active MFA login session'));
    }

    // Backend expects: username, challenge_name, code, session_token
    // These values are persisted during login when response.session_token is returned.
    const username = this.getLoginMFAUsername() ?? undefined;
    const challengeName = this.getLoginMFAChallengeName() ?? undefined;

    return this.http.post(
      `${this.API_URL}${API_ENDPOINTS.AUTH.LOGIN_MFA}`,
      {
        username,
        challenge_name: challengeName,
        code,
        session_token: sessionToken,
      }
    ).pipe(

      tap((response: any) => {
        const data = response?.data ?? {};
        // Backend may return access_token (older flow) and/or session_token (new flow)
        if (response.access_token || response.session_token || data.access_token || data.session_token) {
          this.clearMFAState();
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  private normalizeAvailableMethods(response: any): MFAMethod[] {
    const data = response?.data ?? {};
    const available = response?.available ?? data?.available ?? response?.channels ?? data?.channels ?? [];

    if (!Array.isArray(available)) {
      return [];
    }

    return available
      .map(method => this.normalizeMethod(method))
      .filter((method): method is MFAMethod => !!method);
  }

  private updateLoginMFAContext(response: any, fallbackMethod: MFAMethod): void {
    const data = response?.data ?? {};
    const sessionToken = response?.session_token ?? response?.sessionToken ?? data?.session_token ?? data?.sessionToken;
    const challengeName = response?.challenge_name ?? response?.challengeName ?? data?.challenge_name ?? data?.challengeName ?? this.getRecoveryChallenge(fallbackMethod);

    if (sessionToken) {
      this.setLoginMFASession(sessionToken, fallbackMethod);
    }
    if (challengeName) {
      localStorage.setItem(this.loginMfaChallengeNameKey, String(challengeName));
    }
  }

  private normalizeMethod(method: any): MFAMethod | null {
    const value = String(method ?? '').toUpperCase();
    if (value.includes('SOFTWARE_TOKEN') || value.includes('TOTP') || value.includes('AUTHENTICATOR')) return MFAMethod.TOTP;
    if (value.includes('WHATSAPP')) return MFAMethod.WHATSAPP;
    if (value.includes('SMS') || value.includes('TEXT')) return MFAMethod.SMS;
    if (value.includes('EMAIL') || value.includes('GMAIL')) return MFAMethod.EMAIL;
    return null;
  }

  private getRecoveryChallenge(method: MFAMethod): string {
    if (method === MFAMethod.WHATSAPP) return 'whatsapp';
    if (method === MFAMethod.SMS) return 'sms';
    if (method === MFAMethod.EMAIL) return 'email';
    return 'totp';
  }

  /**
   * Verify TOTP code locally (for UI validation before sending to backend)
   * @param secret The TOTP secret
   * @param token The 6-digit code from authenticator app
   */
  verifyTOTPLocally(secret: string, token: string): boolean {
    try {
      return !!verifySync({ token, secret });
    } catch (error) {
      return false;
    }
  }

  /**
   * Verify and enable TOTP for user
   * @param userId User ID
   * @param secret TOTP secret
   * @param verificationCode 6-digit code from authenticator
   */
  enableTOTP(userId: string, secret: string, verificationCode: string): Observable<MFAResponse> {
    // Verify locally first
    if (!this.verifyTOTPLocally(secret, verificationCode)) {
      return throwError(() => new Error('Invalid verification code'));
    }

    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_TOTP_ENABLE}`,
      { userId, secret, verificationCode }
    ).pipe(
      tap(response => {
        if (response.success) {
          this.updateUserMFASettings(userId, MFAMethod.TOTP, true);
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  /** ═════════════════════════════════════════════════════════
   * MFA Challenge Handling (SMS, Email, TOTP)
   * ═════════════════════════════════════════════════════════ */

  /**
   * Handle MFA challenge returned from login
   */
  handleMFAChallenge(challenge: MFAChallenge): void {
    this._mfaChallenge.set(challenge);
    this._mfaSession.set(challenge.session);
    this._mfaRequired.set(true);

    // Determine which method is being challenged
    if (challenge.challengeName.includes('SMS')) {
      this._activeMFAMethod.set(MFAMethod.SMS);
    } else if (challenge.challengeName.includes('EMAIL')) {
      this._activeMFAMethod.set(MFAMethod.EMAIL);
    } else if (challenge.challengeName.includes('SOFTWARE_TOKEN')) {
      this._activeMFAMethod.set(MFAMethod.TOTP);
    }

    this._mfaStateChange$.next({ stage: 'required' });
    localStorage.setItem('bluedop_mfa_session', challenge.session);
  }

  /**
   * Verify MFA code during login
   */
  verifyMFACode(code: string, method: MFAMethod): Observable<any> {
    const session = this._mfaSession();
    const challenge = this._mfaChallenge();

    if (!session || !challenge) {
      return throwError(() => new Error('No active MFA challenge'));
    }

    return this.http.post(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_VERIFY}`,
      {
        code,
        method,
        session,
        challengeName: challenge.challengeName
      }
    ).pipe(
      tap((response: any) => {
        if (response.access_token) {
          this.clearMFAState();
          this._mfaStateChange$.next({ stage: 'verified' });
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Resend MFA code (SMS or Email)
   */
  resendMFACode(): Observable<MFAResponse> {
    const session = this._mfaSession();
    const challenge = this._mfaChallenge();
    const method = this._activeMFAMethod();

    if (!session || !challenge || !method) {
      return throwError(() => new Error('Cannot resend code without active MFA session'));
    }

    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_RESEND_CODE}`,
      {
        session,
        challengeName: challenge.challengeName,
        method
      }
    ).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /** ═════════════════════════════════════════════════════════
   * User MFA Settings Management
   * ═════════════════════════════════════════════════════════ */

  /**
   * Get user's MFA settings
   */
  getUserMFASettings(userId: string): Observable<UserMFASettings> {
    return this.http.get<UserMFASettings>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_USER_SETTINGS}`.replace('{userId}', userId)
    ).pipe(
      tap(settings => {
        this._userMFASettings.set(settings);
        localStorage.setItem('bluedop_mfa_settings', JSON.stringify(settings));
      }),
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Enable MFA for user
   */
  enableMFA(userId: string, methods: MFAMethod[]): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_ENABLE}`,
      { userId, methods }
    ).pipe(
      tap(response => {
        if (response.success) {
          const settings = this._userMFASettings();
          if (settings) {
            settings.mfaEnabled = true;
            settings.enabledMethods = methods;
            this._userMFASettings.set(settings);
            localStorage.setItem('bluedop_mfa_settings', JSON.stringify(settings));
          }
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Disable MFA for user
   */
  disableMFA(userId: string): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_DISABLE}`,
      { userId }
    ).pipe(
      tap(response => {
        if (response.success) {
          const settings = this._userMFASettings();
          if (settings) {
            settings.mfaEnabled = false;
            settings.enabledMethods = [];
            this._userMFASettings.set(settings);
            localStorage.setItem('bluedop_mfa_settings', JSON.stringify(settings));
          }
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Update preferred MFA method
   */
  setPreferredMFAMethod(userId: string, method: MFAMethod): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_SET_PREFERRED}`,
      { userId, method }
    ).pipe(
      tap(response => {
        if (response.success) {
          const settings = this._userMFASettings();
          if (settings) {
            settings.preferredMFAMethod = method;
            this._userMFASettings.set(settings);
            localStorage.setItem('bluedop_mfa_settings', JSON.stringify(settings));
          }
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Remove a specific MFA method
   */
  removeMFAMethod(userId: string, method: MFAMethod): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_REMOVE_METHOD}`,
      { userId, method }
    ).pipe(
      tap(response => {
        if (response.success) {
          const settings = this._userMFASettings();
          if (settings) {
            settings.enabledMethods = settings.enabledMethods.filter(m => m !== method);
            if (settings.preferredMFAMethod === method) {
              settings.preferredMFAMethod = settings.enabledMethods[0] || null;
            }
            this._userMFASettings.set(settings);
            localStorage.setItem('bluedop_mfa_settings', JSON.stringify(settings));
          }
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  /** ═════════════════════════════════════════════════════════
   * Recovery Codes & Backup
   * ═════════════════════════════════════════════════════════ */

  /**
   * Generate backup recovery codes
   */
  generateBackupCodes(userId: string): Observable<string[]> {
    return this.http.post<{ codes: string[] }>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_BACKUP_CODES}`,
      { userId }
    ).pipe(
      tap(response => {
        const settings = this._userMFASettings();
        if (settings) {
          settings.backupCodes = response.codes;
          this._userMFASettings.set(settings);
          localStorage.setItem('bluedop_mfa_settings', JSON.stringify(settings));
        }
      }),
      switchMap(response => [response.codes]),
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Use a recovery code (one-time use)
   */
  useRecoveryCode(userId: string, code: string): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_USE_RECOVERY_CODE}`,
      { userId, code }
    ).pipe(
      tap(response => {
        if (response.success) {
          // Update backup codes list (server should remove used code)
          this.getUserMFASettings(userId).subscribe();
        }
      }),
      catchError(err => this.handleError(err))
    );
  }

  /** ═════════════════════════════════════════════════════════
   * Trusted Devices Management
   * ═════════════════════════════════════════════════════════ */

  /**
   * Remember device (trust this device to skip MFA for 30 days)
   */
  trustDevice(userId: string): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_TRUST_DEVICE}`,
      { userId, deviceId: this.getDeviceId() }
    ).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Get user's trusted devices
   */
  getTrustedDevices(userId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_TRUSTED_DEVICES}`.replace('{userId}', userId)
    ).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /**
   * Revoke a trusted device
   */
  revokeTrustedDevice(userId: string, deviceId: string): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_REVOKE_DEVICE}`,
      { userId, deviceId }
    ).pipe(
      catchError(err => this.handleError(err))
    );
  }

  /** ═════════════════════════════════════════════════════════
   * Helper Methods
   * ═════════════════════════════════════════════════════════ */

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('bluedop_device_id');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('bluedop_device_id', deviceId);
    }
    return deviceId;
  }

  private updateUserMFASettings(userId: string, method: MFAMethod, enabled: boolean): void {
    const settings = this._userMFASettings();
    if (settings) {
      if (enabled && !settings.enabledMethods.includes(method)) {
        settings.enabledMethods.push(method);
      } else if (!enabled) {
        settings.enabledMethods = settings.enabledMethods.filter(m => m !== method);
      }
      this._userMFASettings.set(settings);
      localStorage.setItem('bluedop_mfa_settings', JSON.stringify(settings));
    }
  }

  private clearMFAState(): void {
    this._mfaRequired.set(false);
    this._mfaSession.set(null);
    this._mfaChallenge.set(null);
    this._activeMFAMethod.set(null);
    localStorage.removeItem('bluedop_mfa_session');
    localStorage.removeItem(this.loginMfaSessionKey);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('MFA Service Error:', error);
    return throwError(() => ({
      message: error.error?.message || error.error?.detail || 'MFA operation failed',
      status: error.status,
      error: error.error
    }));
  }

  isMFARequired(): boolean {
    return this._mfaRequired();
  }

  getActiveMFAMethod(): MFAMethod | null {
    return this._activeMFAMethod();
  }

  getMFAStatus(): Observable<any> {
    const accessToken = this.getSetupAccessToken();
    const options = accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined;

    return this.http.get<any>(
      `${this.API_URL}${API_ENDPOINTS.AUTH.MFA_STATUS}`,
      options
    ).pipe(
      catchError(err => this.handleError(err))
    );
  }

  clearAllMFAState(): void {
    this.clearMFAState();
    this._userMFASettings.set(null);
    this._totpSecret.set(null);
    localStorage.removeItem('bluedop_mfa_settings');
    localStorage.removeItem('bluedop_device_id');
    localStorage.removeItem(this.loginAccessTokenKey);
  }
}
